// Player beacons — a light column over every OTHER human player, so you can
// find each other across the valley. Combat OFF only (same flag as
// playermap.js): with guns live a visible-from-anywhere beam is a target
// marker. Distance-INVERTED: invisible inside tunables.map.beacon_hide_m,
// full from beacon_full_m outward — up close the avatar and its chest nametag take over
// and a bright column would only hide the person. Width is SCREEN-constant
// (≈beacon_px pixels), never below a real 0.5 m, so it still reads 2 km out.
// Look = the station halo's fresnel shell (stationzones.js _pulseMat) in the
// player's hue, fading upward. ONE instanced mesh = one draw call; labels
// (name · distance) are one screen-size sprite each, drawn over terrain.
// A SECOND pool of one: the inspect card's waypoint (setWaypoint) — a gold
// column at a ground point, the same instanced mesh (no extra draw call),
// its own label; hidden within WP_HIDE_M of the eye (you have arrived).
// 🧭 Guide me (MAP-D, owner R6 "Follow along"): a RouteGuide drives that same
// single waypoint ~GUIDE.ahead_m ahead along a route stage as you walk
// (main.js ticks guideTick every GUIDE.period_ms; the compass strip marker
// follows the waypoint). Any other setWaypoint / clearWaypoint cancels it.
import * as THREE from 'three';
import { t } from './i18n.js';
import './i18n/player.js';
import { playerHue, fmtDist } from './playermap.js';
import { polyArc, pointAtArc, projectOnPoly } from './trailmarks.js';

const MAX = 24;                  // gs_max_players hard ceiling
const WP_HUE = 44;               // waypoint gold (hsl hue)
const WP_HIDE_M = 8, WP_FULL_M = 30;
const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _s = new THREE.Vector3();
const _p = new THREE.Vector3(), _eye = new THREE.Vector3(), _c = new THREE.Color();

// the route guide's feel: look-ahead, arrival radius, tick period, the arc
// window a re-projection searches around the last progress (a loop / a
// switchback that passes close by does not steal the progress), and how far
// off the line the player may stray before the whole stage is searched again
export const GUIDE = { ahead_m: 150, arrive_m: 15, period_ms: 500, back_m: 120, fwd_m: 1200, rejoin_m: 80 };

// Follow-along on one stage polyline (xz interleaved, route direction). dir
// -1 walks it backwards. step(x, z) → {x, z (the waypoint), end (clamped
// at the stage end), s (progress m), left (m)} or {arrived: true} within
// GUIDE.arrive_m of the end once the progress is past the last look-ahead.
// Pure (no three): scripts/check-trails.mjs runs it on a fixture.
export class RouteGuide {
  constructor(pts, { dir = 1, label = '', ri = -1, si = 0 } = {}) {
    const n = pts.length >> 1;
    const P = new Float64Array(2 * n);
    for (let i = 0; i < n; i++) {
      const j = dir < 0 ? n - 1 - i : i;
      P[2 * i] = pts[2 * j]; P[2 * i + 1] = pts[2 * j + 1];
    }
    this.P = P; this.A = polyArc(P); this.L = this.A[n - 1];
    this.dir = dir < 0 ? -1 : 1; this.label = label; this.ri = ri; this.si = si;
    this.s = null;
  }

  // the direction a player at (x, z) would walk a stage: towards the farther
  // end (the nearer end is where they start); a loop (ends ≤ 50 m apart) → +1
  static dirFor(pts, x, z) {
    const n = pts.length >> 1;
    const ds = Math.hypot(x - pts[0], z - pts[1]), de = Math.hypot(x - pts[2 * n - 2], z - pts[2 * n - 1]);
    const loop = Math.hypot(pts[0] - pts[2 * n - 2], pts[1] - pts[2 * n - 1]) <= 50;
    return loop || ds <= de ? 1 : -1;
  }

  step(x, z) {
    const G = GUIDE, P = this.P, A = this.A;
    let p = this.s !== null ? projectOnPoly(P, A, x, z, this.s - G.back_m, this.s + G.fwd_m) : null;
    if (!p || p.d > G.rejoin_m) {
      const q = projectOnPoly(P, A, x, z);
      if (!p || (q && q.d < p.d)) p = q;
    }
    if (!p) return { arrived: true };
    this.s = p.s;
    const n = P.length >> 1;
    const dEnd = Math.hypot(x - P[2 * n - 2], z - P[2 * n - 1]);
    if (dEnd <= G.arrive_m && p.s >= this.L - G.ahead_m - G.arrive_m) return { arrived: true, s: p.s };
    const t = Math.min(this.L, p.s + G.ahead_m);
    const [tx, tz] = pointAtArc(P, A, t);
    return { x: tx, z: tz, end: t >= this.L - 1e-6, s: p.s, left: this.L - p.s };
  }
}

export class Beacons {
  constructor(scene) {
    const geo = new THREE.CylinderGeometry(1, 1, 1, 16, 1, true);
    geo.translate(0, 0.5, 0);    // base at y=0, top at y=1
    this._fade = new THREE.InstancedBufferAttribute(new Float32Array(MAX + 1), 1);
    this._col = new THREE.InstancedBufferAttribute(new Float32Array((MAX + 1) * 3), 3);
    geo.setAttribute('aFade', this._fade);
    geo.setAttribute('aCol', this._col);
    this.mat = new THREE.ShaderMaterial({
      uniforms: { uTime: { value: 0 } },
      vertexShader: `attribute float aFade; attribute vec3 aCol;
        varying vec3 vN; varying vec3 vW; varying vec3 vCol;
        varying float vFade; varying float vUp;
        void main() {
          mat4 m = modelMatrix * instanceMatrix;
          vN = normalize(mat3(m) * normal);
          vec4 w = m * vec4(position, 1.0);
          vW = w.xyz; vUp = position.y; vCol = aCol; vFade = aFade;
          gl_Position = projectionMatrix * viewMatrix * w;
        }`,
      fragmentShader: `uniform float uTime;
        varying vec3 vN; varying vec3 vW; varying vec3 vCol;
        varying float vFade; varying float vUp;
        void main() {
          vec3 V = normalize(cameraPosition - vW);
          float f = 1.0 - abs(dot(normalize(vN), V));
          float glow = pow(f, 2.4);
          float pulse = 0.82 + 0.18 * sin(uTime * 1.5);
          float top = 1.0 - smoothstep(0.3, 1.0, vUp);
          float a = (0.34 + 0.5 * glow) * pulse * top * vFade;
          gl_FragColor = vec4(vCol, min(a, 0.9));
        }`,
      transparent: true, depthWrite: false, side: THREE.DoubleSide,
    });
    this.mesh = new THREE.InstancedMesh(geo, this.mat, MAX + 1);
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;   // instances span the world
    this.mesh.renderOrder = 5;
    scene.add(this.mesh);
    this.scene = scene;
    this.labels = new Map();           // id -> { sprite, canvas, tex, text }
    this.cfg = {};
    this.waypoint = null;              // {x, y, z, label, guide?} (inspect card 📍 / 🧭)
    this._wpLabel = null;
    this.guide = null;                 // RouteGuide (🧭 Guide me on a route)
  }

  // the inspect card's waypoint: one gold column at (x, y, z) — y = the
  // ground there. Replaces any previous one (and ends a route guide).
  setWaypoint(x, z, label = '', y = 0) {
    this.guide = null;
    this.waypoint = { x, y, z, label: String(label || 'waypoint') };
  }

  clearWaypoint() {
    this.guide = null;
    this.waypoint = null;
    if (this._wpLabel) this._wpLabel.sprite.visible = false;
  }

  // 🧭 follow a route stage: replaces any waypoint / guide; the first
  // guideTick places the waypoint
  startGuide(g) {
    this.clearWaypoint();
    this.guide = g;
  }

  // main.js, every GUIDE.period_ms while a guide runs (combat off): move the
  // waypoint ahead; ground(x, z) → y. On arrival the guide and the waypoint
  // clear. → the step result ({arrived} / {x, z, end, left}) or null
  guideTick(x, z, ground) {
    const g = this.guide;
    if (!g) return null;
    const r = g.step(x, z);
    if (r.arrived) { this.clearWaypoint(); return r; }
    const y = ground ? ground(r.x, r.z) : null;
    this.waypoint = { x: r.x, y: y ?? 0, z: r.z, label: r.end ? `${g.label} · end` : g.label, guide: true };
    return r;
  }

  // the same point (within 2 m) is already the waypoint
  isWaypoint(x, z) {
    const w = this.waypoint;
    return !!w && Math.hypot(w.x - x, w.z - z) < 2;
  }

  applyTunables(t) { this.cfg = (t && t.map) || {}; }

  _label(id) {
    let L = id === 'wp' ? this._wpLabel : this.labels.get(id);
    if (L) return L;
    const canvas = document.createElement('canvas');
    canvas.width = 320; canvas.height = 64;
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex,
      depthTest: false, depthWrite: false, transparent: true, sizeAttenuation: false }));
    sprite.renderOrder = 998;
    sprite.center.set(0.5, 0);
    this.scene.add(sprite);
    L = { sprite, canvas, tex, text: null };
    if (id === 'wp') this._wpLabel = L; else this.labels.set(id, L);
    return L;
  }

  _draw(L, id, text) {
    if (L.text === text) return;
    L.text = text;
    const g = L.canvas.getContext('2d');
    g.clearRect(0, 0, 320, 64);
    g.font = '600 28px system-ui, sans-serif';
    const w = Math.min(304, g.measureText(text).width + 52);
    const x0 = (320 - w) / 2;
    g.fillStyle = 'rgba(10,14,22,.78)';
    g.beginPath(); g.roundRect(x0, 8, w, 48, 24); g.fill();
    g.fillStyle = `hsl(${id === 'wp' ? WP_HUE : playerHue(id)} 75% 60%)`;
    g.beginPath(); g.arc(x0 + 24, 32, 9, 0, Math.PI * 2); g.fill();
    g.fillStyle = '#fff'; g.textBaseline = 'middle';
    g.fillText(text, x0 + 40, 33, w - 48);
    L.tex.needsUpdate = true;
  }

  // o: { enabled, remotes (net.remotes), cam (world-space eye camera),
  //      radPerPx (view angle per screen pixel), followId, time }
  update(o) {
    let n = 0;
    const seen = new Set();
    if (o.enabled) {
      const f0 = this.cfg.beacon_hide_m ?? 25, f1 = this.cfg.beacon_full_m ?? 60;
      const H = this.cfg.beacon_height_m || 250;
      const wantPx = this.cfg.beacon_px || 3;
      const elev = Math.tan(((this.cfg.label_elev_deg ?? 4) * Math.PI) / 180);
      o.cam.getWorldPosition(_eye);
      for (const [id, r] of o.remotes) {
        if (n >= MAX) break;
        if (r.bot || !r.group || !r.ent || !r.ent.alive) continue;
        const gp = r.group.position;
        const d = Math.hypot(gp.x - _eye.x, gp.z - _eye.z);
        const fol = id === o.followId;
        const k = THREE.MathUtils.smoothstep(d, f0, f1);
        if (k <= 0.001) continue;
        const rad = Math.max(0.5, d * o.radPerPx * wantPx * 0.5) * (fol ? 1.8 : 1);
        _p.set(gp.x, gp.y - 1, gp.z);
        _s.set(rad, H, rad);
        this.mesh.setMatrixAt(n, _m.compose(_p, _q, _s));
        _c.setHSL(playerHue(id) / 360, 0.75, fol ? 0.7 : 0.6);
        this._col.setXYZ(n, _c.r, _c.g, _c.b);
        this._fade.setX(n, fol ? Math.min(1, k * 1.6) : k);
        n++;
        // label: a fixed angle above the eye's horizon, kept inside the
        // visible (lower) part of the column; screen-constant size
        const L = this._label(id);
        seen.add(id);
        this._draw(L, id, `${r.ent.n || t('player.beacon.player', { id })} · ${fmtDist(d)}`);
        const ly = Math.min(Math.max(_eye.y + d * elev, gp.y + 3), gp.y + H * 0.55);
        L.sprite.position.set(gp.x, ly, gp.z);
        const hgt = fol ? 0.05 : 0.038;
        L.sprite.scale.set(hgt * 5, hgt, 1);
        L.sprite.material.opacity = k;
        L.sprite.visible = true;
      }
    }
    // the waypoint: same column look, gold, after the players (combat off too)
    const w = this.waypoint;
    let wpShown = false;
    if (o.enabled && w) {
      const d = Math.hypot(w.x - _eye.x, w.z - _eye.z);
      const k = THREE.MathUtils.smoothstep(d, WP_HIDE_M, WP_FULL_M);
      if (k > 0.001) {
        const H = this.cfg.beacon_height_m || 250;
        const rad = Math.max(0.5, d * o.radPerPx * (this.cfg.beacon_px || 3) * 0.5) * 1.4;
        _p.set(w.x, w.y - 1, w.z);
        _s.set(rad, H, rad);
        this.mesh.setMatrixAt(n, _m.compose(_p, _q, _s));
        _c.setHSL(WP_HUE / 360, 0.85, 0.62);
        this._col.setXYZ(n, _c.r, _c.g, _c.b);
        this._fade.setX(n, Math.min(1, k * 1.4));
        n++;
        const L = this._label('wp');
        this._draw(L, 'wp', `📍 ${w.label} · ${fmtDist(d)}`);
        const elev = Math.tan(((this.cfg.label_elev_deg ?? 4) * Math.PI) / 180);
        L.sprite.position.set(w.x, Math.min(Math.max(_eye.y + d * elev, w.y + 3), w.y + H * 0.55), w.z);
        L.sprite.scale.set(0.045 * 5, 0.045, 1);
        L.sprite.material.opacity = k;
        L.sprite.visible = true;
        wpShown = true;
      }
    }
    if (this._wpLabel && !wpShown) this._wpLabel.sprite.visible = false;
    this.mesh.count = n;
    this.mesh.instanceMatrix.needsUpdate = true;
    this._fade.needsUpdate = true;
    this._col.needsUpdate = true;
    this.mat.uniforms.uTime.value = o.time;
    for (const [id, L] of this.labels) {
      if (seen.has(id)) continue;
      if (o.remotes.has(id)) { L.sprite.visible = false; continue; }
      this.scene.remove(L.sprite);           // player left: free the label
      L.tex.dispose(); L.sprite.material.dispose();
      this.labels.delete(id);
    }
  }
}
